import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Bold, Check, Clock3, FileText, Heading2, Quote, Sparkles, X } from 'lucide-react';
import { SectionCard } from './PeecUI';
import { useI18n } from '../i18n/useI18n';

export type EarnedDraftStatus = 'done' | 'running';
export type EarnedDraftMaterialSource = 'auto' | 'brand-library' | 'partner-assets';
export type EarnedDraftWorkflowStepState = 'done' | 'running' | 'pending';

export interface EarnedDraftWorkflowStep {
  id: string;
  label: string;
  note?: string;
  state: EarnedDraftWorkflowStepState;
}

export interface EarnedDraftDetail {
  id: string;
  title: string;
  phrase: string;
  status: EarnedDraftStatus;
  headline: string;
  summary: string;
  editorContent: string;
  workflowSteps: EarnedDraftWorkflowStep[];
  materialSource: EarnedDraftMaterialSource;
  updatedAt: string;
}

interface EarnedDraftDetailDialogProps {
  draft: EarnedDraftDetail | null;
  isOpen: boolean;
  onClose: () => void;
}

const MATERIAL_SOURCE_KEY: Record<EarnedDraftMaterialSource, string> = {
  auto: 'earned.materialAuto',
  'brand-library': 'earned.materialBrandLibrary',
  'partner-assets': 'earned.materialPartnerAssets',
};

const STATUS_KEY: Record<EarnedDraftStatus, string> = {
  running: 'earned.taskRunning',
  done: 'earned.taskDone',
};

const WORKFLOW_STATE_KEY: Record<EarnedDraftWorkflowStepState, string> = {
  done: 'earned.detailWorkflowDone',
  running: 'earned.detailWorkflowRunning',
  pending: 'earned.detailWorkflowPending',
};

export const EarnedDraftDetailDialog: React.FC<EarnedDraftDetailDialogProps> = ({
  draft,
  isOpen,
  onClose,
}) => {
  const { language, t } = useI18n();
  const editorRef = useRef<HTMLTextAreaElement | null>(null);
  const [editorHeadline, setEditorHeadline] = useState('');
  const [editorContent, setEditorContent] = useState('');

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!draft) {
      return;
    }

    setEditorHeadline(draft.headline);
    setEditorContent(draft.editorContent);
  }, [draft]);

  const formattedUpdatedAt = useMemo(() => {
    if (!draft) {
      return '';
    }

    return new Intl.DateTimeFormat(language === 'zh' ? 'zh-CN' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(draft.updatedAt));
  }, [draft, language]);

  const workflowProgress = useMemo(() => {
    if (!draft || draft.workflowSteps.length === 0) {
      return 0;
    }

    const completedCount = draft.workflowSteps.filter((step) => step.state === 'done').length;
    return Math.round((completedCount / draft.workflowSteps.length) * 100);
  }, [draft]);

  const applyEditorFormat = (mode: 'heading' | 'bold' | 'quote') => {
    const textarea = editorRef.current;
    if (!textarea) {
      return;
    }

    const selectionStart = textarea.selectionStart;
    const selectionEnd = textarea.selectionEnd;
    const selectedText = editorContent.slice(selectionStart, selectionEnd);
    const fallbackText = t('earned.detailToolbarPlaceholder');
    const activeText = selectedText || fallbackText;

    const formattedText =
      mode === 'heading'
        ? `## ${activeText}`
        : mode === 'bold'
        ? `**${activeText}**`
        : `> ${activeText}`;

    const nextContent = `${editorContent.slice(0, selectionStart)}${formattedText}${editorContent.slice(selectionEnd)}`;
    setEditorContent(nextContent);

    requestAnimationFrame(() => {
      textarea.focus();
      const cursorEnd = selectionStart + formattedText.length;
      textarea.setSelectionRange(cursorEnd, cursorEnd);
    });
  };

  if (!isOpen || !draft) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="earned-draft-detail-title"
      aria-label={t('earned.detailDialogLabel')}
      onClick={onClose}
    >
      <div
        className="flex max-h-full w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-md"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-zinc-200 px-5 py-4">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-zinc-400">
              {draft.title}
            </p>
            <h3 id="earned-draft-detail-title" className="mt-1 text-xl font-semibold tracking-tight text-zinc-950">
              {draft.headline}
            </h3>
            <p className="mt-1.5 text-sm leading-relaxed text-zinc-500">{draft.summary}</p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-zinc-200 text-zinc-500 outline-none transition-colors hover:bg-zinc-50 focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>

        <div className="min-h-0 overflow-y-auto overscroll-contain px-5 py-5">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
            <SectionCard className="border border-zinc-200">
              <div className="space-y-4 px-4 py-4">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-zinc-400">
                    {t('earned.detailStatus')}
                  </p>
                  <p className="mt-1 text-sm font-medium text-zinc-900">{t(STATUS_KEY[draft.status])}</p>
                </div>

                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-zinc-400">
                    {t('earned.detailKeyword')}
                  </p>
                  <p className="mt-1 text-sm text-zinc-700">{draft.phrase}</p>
                </div>

                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-zinc-400">
                    {t('earned.detailMaterial')}
                  </p>
                  <p className="mt-1 text-sm text-zinc-700">{t(MATERIAL_SOURCE_KEY[draft.materialSource])}</p>
                </div>

                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-zinc-400">
                    {t('earned.detailUpdatedAt')}
                  </p>
                  <p className="mt-1 inline-flex items-center gap-1.5 text-sm text-zinc-700">
                    <Clock3 size={14} className="text-zinc-400" aria-hidden="true" />
                    {formattedUpdatedAt}
                  </p>
                </div>

                <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-widest text-zinc-400">
                        {t('earned.detailWorkflow')}
                      </p>
                      <p className="mt-1 text-sm font-medium text-zinc-900">{workflowProgress}%</p>
                    </div>
                    <span className="rounded-full border border-zinc-200 bg-white px-2.5 py-1 text-[11px] font-medium text-zinc-600">
                      {draft.workflowSteps.filter((step) => step.state === 'done').length}/{draft.workflowSteps.length}
                    </span>
                  </div>
                  <div className="mt-3 h-2 overflow-hidden rounded-full bg-zinc-200">
                    <div
                      className="h-full rounded-full bg-zinc-900 transition-[width] duration-200"
                      style={{ width: `${workflowProgress}%` }}
                    />
                  </div>
                </div>

                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-zinc-400">
                    {t('earned.detailWorkflow')}
                  </p>
                  <ol className="mt-3 space-y-2">
                    {draft.workflowSteps.map((step, index) => {
                      const indicatorClassName =
                        step.state === 'done'
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-600'
                          : step.state === 'running'
                          ? 'border-zinc-300 bg-zinc-100 text-zinc-700'
                          : 'border-zinc-200 bg-white text-zinc-400';

                      return (
                        <li key={step.id} className="rounded-xl border border-zinc-200 px-3 py-3">
                          <div className="flex items-start gap-3">
                            <span
                              className={`mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold ${indicatorClassName}`}
                            >
                              {step.state === 'done' ? <Check size={12} aria-hidden="true" /> : String(index + 1).padStart(2, '0')}
                            </span>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-zinc-900">{step.label}</p>
                                <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-medium text-zinc-600">
                                  {t(WORKFLOW_STATE_KEY[step.state])}
                                </span>
                              </div>
                              {step.note ? <p className="mt-1 text-xs leading-relaxed text-zinc-500">{step.note}</p> : null}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                </div>
              </div>
            </SectionCard>

            <SectionCard className="border border-zinc-200">
              <div className="flex items-center justify-between gap-3 border-b border-zinc-200 px-4 py-3">
                <div className="flex items-center gap-2">
                  <FileText size={15} className="text-zinc-400" aria-hidden="true" />
                  <div>
                    <h4 className="text-sm font-semibold text-zinc-950">{t('earned.detailEditor')}</h4>
                    <p className="text-xs text-zinc-500">{t('earned.detailEditorHint')}</p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => applyEditorFormat('heading')}
                    aria-label={t('earned.detailToolbarHeading')}
                    className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 text-xs font-medium text-zinc-700 outline-none transition-colors hover:bg-zinc-50 focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
                  >
                    <Heading2 size={13} aria-hidden="true" />
                    {t('earned.detailToolbarHeading')}
                  </button>
                  <button
                    type="button"
                    onClick={() => applyEditorFormat('bold')}
                    aria-label={t('earned.detailToolbarBold')}
                    className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 text-xs font-medium text-zinc-700 outline-none transition-colors hover:bg-zinc-50 focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
                  >
                    <Bold size={13} aria-hidden="true" />
                    {t('earned.detailToolbarBold')}
                  </button>
                  <button
                    type="button"
                    onClick={() => applyEditorFormat('quote')}
                    aria-label={t('earned.detailToolbarQuote')}
                    className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 text-xs font-medium text-zinc-700 outline-none transition-colors hover:bg-zinc-50 focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
                  >
                    <Quote size={13} aria-hidden="true" />
                    {t('earned.detailToolbarQuote')}
                  </button>
                  <span className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-zinc-50 px-3 text-xs font-medium text-zinc-500">
                    <Sparkles size={13} aria-hidden="true" />
                    {t('earned.detailEditorMeta')}
                  </span>
                </div>
              </div>

              <div className="space-y-4 px-4 py-4">
                <div>
                  <label
                    htmlFor="earned-draft-editor-headline"
                    className="text-[11px] font-semibold uppercase tracking-widest text-zinc-400"
                  >
                    {t('earned.detailHeadline')}
                  </label>
                  <input
                    id="earned-draft-editor-headline"
                    name="earned-draft-editor-headline"
                    autoComplete="off"
                    value={editorHeadline}
                    onChange={(event) => setEditorHeadline(event.target.value)}
                    className="mt-2 h-11 w-full rounded-xl border border-zinc-200 px-3 text-sm font-medium text-zinc-950 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
                  />
                </div>

                <div>
                  <label
                    htmlFor="earned-draft-editor-content"
                    className="text-[11px] font-semibold uppercase tracking-widest text-zinc-400"
                  >
                    {t('earned.detailPreview')}
                  </label>
                  <textarea
                    id="earned-draft-editor-content"
                    ref={editorRef}
                    name="earned-draft-editor-content"
                    value={editorContent}
                    onChange={(event) => setEditorContent(event.target.value)}
                    spellCheck={false}
                    className="mt-2 min-h-[520px] w-full resize-none rounded-xl border border-zinc-200 px-4 py-4 text-sm leading-7 text-zinc-800 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
                  />
                </div>
              </div>
            </SectionCard>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EarnedDraftDetailDialog;